// Technical Estimates - guide section 21 ("From quantities to estimate").
//
// Owner company only: an estimate is the company's pricing of its own
// work. Every number on this page comes from the server (the same build
// that saves the draft and renders the PDF), so the preview, the saved
// draft and the documents can never disagree.
//
// Quantities are entered here by rate key, or arrive from a technical
// view's register (?view=&version=, sent by components/Coordination/
// EstimatePanel on the view page; mapped server-side by utils/estimate/fromView).
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert, Button, Card, Checkbox, Col, Collapse, Empty, Input, InputNumber, Row, Select, Space, Table, Tag,
  Tooltip, Typography, message,
} from "antd";
import { api, download, hrs, PreviewPanel } from "./estimateShared";
import CoordinationBreadcrumbs from "components/Coordination/CoordinationBreadcrumbs";
import { useRememberRouteProject } from "helpers/projectSelection";
import { matchOption } from "components/SearchSelect/searchSelect";

const CONFIDENCE = ["measured", "estimated", "allowance", "not located"];
const blankRow = () => ({ key: Math.random().toString(36).slice(2), rateKey: "", qtyForRate: 1, system: "", confidence: "measured", label: "" });

function emptyForm() {
  return {
    seriesId: null, clientName: "", title: "", levels: 1, rows: [blankRow()],
    // Set when the quantities came from a technical view (?view=&version=):
    // the server then records the view's own documents for staleness.
    viewId: "", viewVersion: null, fromView: null,
    skip: {}, reasons: {}, coordination: { rounds: 3, meetings: 0, rfis: 0 }, oversight: { percent: 0 },
    windowWeeks: 0, alternates: [], bundle: { minCount: 2, percent: 0 }, selectedAlternates: [], targetFee: null, notes: "",
  };
}

export default function EstimatesPage() {
  const { projectId } = useParams();
  useRememberRouteProject(projectId);
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  const [rates, setRates] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  // Pricing rights on THIS project (admins, the project owner, its PM).
  const [pricing, setPricing] = useState({ ok: false });

  const load = () => {
    api(`/projects/${projectId}/estimates`).then((d) => setList(d.estimates || [])).catch((e) => setErr(e.message));
    api(`/projects/${projectId}/estimates/rates`).then(setRates).catch(() => setRates(null));
    api(`/projects/${projectId}/estimates/rate-card`).then((d) => setPricing(d.pricing || { ok: false })).catch(() => setPricing({ ok: false }));
  };
  useEffect(load, [projectId]); // eslint-disable-line

  // Opened from a technical view's EstimatePanel: its quantities fill the form.
  const location = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(location.search);
    const viewId = q.get("view");
    if (!viewId) return;
    const version = q.get("version");
    api(`/projects/${projectId}/estimates/from-view/${encodeURIComponent(viewId)}${version ? `?version=${encodeURIComponent(version)}` : ""}`)
      .then((d) => setForm((f) => ({
        ...f, viewId: d.view.viewId, viewVersion: d.view.version, levels: d.levels || 1,
        title: f.title || `${d.view.viewId} v${d.view.version}`,
        rows: d.rows.length ? d.rows.map((r) => ({ ...blankRow(), rateKey: r.rateKey, qtyForRate: r.qtyForRate, system: r.system, confidence: r.confidence, label: r.label, level: r.level, evidence: r.evidence || null })) : [blankRow()],
        fromView: { unmapped: d.unmapped || [], sourceDocs: d.sourceDocs || [] },
      })))
      .catch((e) => message.error(`Could not read the view: ${e.message}`));
  }, [projectId, location.search]);

  const rateOptions = useMemo(() => Object.entries((rates && rates.modeling) || {})
    .map(([k, v]) => ({ value: k, label: `${k} - ${v.hoursPer} h/${v.unit || "unit"}${v.source && v.source !== "default" ? ` (${v.source})` : ""}` })), [rates]);
  const skippable = useMemo(() => Object.entries((rates && rates.allowances) || {}).filter(([, a]) => a.skipIf), [rates]);

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setPreview(null); };
  const setRow = (key, patch) => set({ rows: form.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) });

  const body = () => {
    const alternates = form.alternates.filter((a) => a.label && a.hours > 0)
      .map((a) => ({ key: a.key, label: a.label, buckets: { modeling: Number(a.hours) } }));
    return {
      seriesId: form.seriesId || undefined, clientName: form.clientName, title: form.title, levels: form.levels,
      viewId: form.viewId || undefined, viewVersion: form.viewId ? form.viewVersion : undefined,
      rows: form.rows.filter((r) => r.rateKey && r.qtyForRate > 0),
      skip: form.skip, reasons: form.reasons, coordination: form.coordination, oversight: form.oversight,
      windowWeeks: form.windowWeeks,
      options: [
        { key: "model", label: "Modeling only", buckets: ["modeling"] },
        { key: "full", label: "Modeling and coordination", buckets: ["modeling", "coordination", "oversight"] },
      ],
      alternates, selectedAlternates: form.selectedAlternates, bundle: form.bundle.percent > 0 ? form.bundle : null,
      targetFee: form.targetFee || null,
      notes: String(form.notes || "").split("\n").map((s) => s.trim()).filter(Boolean),
    };
  };

  const runPreview = async () => {
    setBusy(true);
    try { setPreview((await api(`/projects/${projectId}/estimates/preview`, { method: "POST", body: JSON.stringify(body()) })).results); }
    catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await api(`/projects/${projectId}/estimates`, { method: "POST", body: JSON.stringify(body()) });
      message.success(`Saved ${r.estimate.reference} as a draft`);
      setForm(emptyForm()); setPreview(null); load();
    } catch (e) { message.error(e.message); } finally { setBusy(false); }
  };
  const approve = async (e) => {
    try { await api(`/projects/${projectId}/estimates/${e._id}/approve`, { method: "POST" }); message.success(`${e.reference} approved`); load(); }
    catch (x) { message.error(x.message); }
  };
  // The list leaves the rows out to stay light, so a revision starts from
  // the FULL saved inputs of that revision, fetched here.
  const revise = async (summary) => {
    let e;
    try { e = (await api(`/projects/${projectId}/estimates/${summary._id}`)).estimate; }
    catch (x) { message.error(x.message); return; }
    const i = e.inputs || {};
    setForm({
      ...emptyForm(), seriesId: e.seriesId, clientName: e.clientCode, title: e.title, levels: i.levels || 1,
      viewId: e.viewId || "", viewVersion: e.viewId ? e.viewVersion : null, fromView: e.viewId ? { unmapped: [], sourceDocs: e.sourceDocs || [] } : null,
      rows: (i.rows || []).map((r) => ({ ...blankRow(), ...r, confidence: typeof r.confidence === "string" ? r.confidence : "measured" })),
      skip: i.skip || {}, reasons: i.reasons || {}, coordination: i.coordination || {}, oversight: i.oversight || {},
      windowWeeks: i.windowWeeks || 0, notes: (i.notes || []).join("\n"),
      alternates: (i.alternates || []).map((a) => ({ key: a.key, label: a.label, hours: (a.buckets || {}).modeling || 0 })),
      selectedAlternates: i.selectedAlternates || [], bundle: i.bundle || { minCount: 2, percent: 0 }, targetFee: i.targetFee || null,
    });
    setPreview(null);
    window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  };

  const revisionColumns = [
    { title: "Reference", dataIndex: "reference", render: (v, e) => <span><b>{v}</b><div style={{ color: "#888", fontSize: 12 }}>{e.title}</div></span> },
    {
      title: "Status", key: "status", width: 190,
      render: (_, e) => (
        <Space size={4} wrap>
          <Tag color={e.status === "approved" ? "green" : e.status === "superseded" ? "default" : "gold"}>{e.status}</Tag>
          {e.staleness && e.staleness.stale ? (
            <Tooltip title={e.staleness.reasons.map((r) => `${r.name || r.docId}: rev ${r.was} -> ${r.now === null ? "removed" : r.now}`).join("; ")}>
              <Tag color="red">stale</Tag>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    { title: "Approved by", key: "approvedBy", width: 170, render: (_, e) => (e.approvedBy && e.approvedBy.name ? `${e.approvedBy.name}, ${String(e.approvedBy.at || "").slice(0, 10)}` : "-") },
    { title: "Modelling", key: "h", width: 100, render: (_, e) => hrs(e.results && e.results.hours && e.results.hours.modeling) },
    {
      title: "", key: "actions", width: 360,
      render: (_, e) => (
        <Space size={2} wrap>
          {["document.pdf", "scope.pdf", "register.csv", "register.xlsx"].map((f) => (
            <Button key={f} size="small" onClick={() => download(`/projects/${projectId}/estimates/${e._id}/${f}`, `${e.reference}-${f}`).catch((x) => message.error(x.message))}>
              {f === "document.pdf" ? "PDF" : f === "scope.pdf" ? "Scope" : f === "register.csv" ? "CSV" : "XLSX"}
            </Button>
          ))}
          {e.status === "draft" ? (
            <Tooltip title={!pricing.ok ? "Approving needs pricing rights on this project (an admin, the project owner or its PM)" : e.staleness && e.staleness.stale ? "A source document was revised - revise the estimate first" : ""}>
              <Button size="small" type="primary" disabled={!pricing.ok || (e.staleness && e.staleness.stale)} onClick={() => approve(e)}>Approve</Button>
            </Tooltip>
          ) : null}
          <Button size="small" type="link" onClick={() => revise(e)}>Revise</Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <CoordinationBreadcrumbs current="Technical estimates" />
      <Typography.Title level={3} style={{ marginBottom: 4 }}>Technical estimates</Typography.Title>
      <Typography.Paragraph type="secondary">
        Quantities to hours to fee to documents. Drafts until someone with pricing rights approves them; stale when a source document is revised.
      </Typography.Paragraph>
      {err ? <Alert type="error" showIcon message={err} style={{ marginBottom: 12 }} /> : null}

      <Card size="small" title="Revisions">
        {list && list.length ? <Table size="small" rowKey="_id" pagination={false} dataSource={list} columns={revisionColumns} />
          : <Empty description={list === null ? "Loading..." : "No estimates yet"} />}
      </Card>

      <PricingPanel projectId={projectId} pricing={pricing} />
      <RateCardEditor projectId={projectId} canEdit={pricing.ok} onSaved={() => setPreview(null)} />

      <Card size="small" style={{ marginTop: 16 }} title={form.seriesId ? "Revise the estimate" : "New estimate"}
            extra={form.seriesId ? <Button size="small" onClick={() => set(emptyForm())}>Start a new one instead</Button> : null}>
        <Row gutter={12}>
          <Col xs={24} md={8}><Typography.Text type="secondary">Client</Typography.Text><Input value={form.clientName} disabled={!!form.seriesId} placeholder="DP Electric" onChange={(e) => set({ clientName: e.target.value })} /></Col>
          <Col xs={24} md={10}><Typography.Text type="secondary">Title</Typography.Text><Input value={form.title} placeholder="Level 3 - telecom and security pathways" onChange={(e) => set({ title: e.target.value })} /></Col>
          <Col xs={24} md={6}><Typography.Text type="secondary">Levels in scope</Typography.Text><InputNumber min={1} value={form.levels} style={{ width: "100%" }} onChange={(v) => set({ levels: v || 1 })} /></Col>
        </Row>

        <Typography.Title level={5} style={{ marginTop: 16 }}>Quantities</Typography.Title>
        {form.viewId ? (
          <Alert style={{ marginBottom: 8 }} type="info" showIcon
                 message={`From technical view ${form.viewId} v${form.viewVersion}${form.fromView && form.fromView.sourceDocs.length ? ` - ${form.fromView.sourceDocs.length} source document(s) kept for staleness` : ""}`}
                 description={form.fromView && form.fromView.unmapped.length ? `Not priced, no production key: ${form.fromView.unmapped.map((u) => `${u.qty} x ${u.type}`).join(", ")}` : undefined} />
        ) : null}
        <Table size="small" pagination={false} rowKey="key" dataSource={form.rows} columns={[
          { title: "Item", dataIndex: "label", render: (v, r) => <Input size="small" value={v} placeholder="2&quot; telecom sleeves" onChange={(e) => setRow(r.key, { label: e.target.value })} /> },
          { title: "Rate", dataIndex: "rateKey", width: 280, render: (v, r) => <Select filterOption={matchOption} size="small" showSearch style={{ width: "100%" }} value={v || undefined} placeholder="rate key" options={rateOptions} onChange={(x) => setRow(r.key, { rateKey: x })} /> },
          { title: "Qty", dataIndex: "qtyForRate", width: 90, render: (v, r) => <InputNumber size="small" min={0} value={v} onChange={(x) => setRow(r.key, { qtyForRate: x || 0 })} /> },
          { title: "System", dataIndex: "system", width: 120, render: (v, r) => <Input size="small" value={v} placeholder="telecom" onChange={(e) => setRow(r.key, { system: e.target.value })} /> },
          { title: "Confidence", dataIndex: "confidence", width: 140, render: (v, r) => <Select size="small" style={{ width: "100%" }} value={v} options={CONFIDENCE.map((c) => ({ value: c, label: c }))} onChange={(x) => setRow(r.key, { confidence: x })} /> },
          { title: "", key: "x", width: 40, render: (_, r) => <Button size="small" type="text" onClick={() => set({ rows: form.rows.filter((x) => x.key !== r.key) })}>x</Button> },
        ]} />
        <Button size="small" style={{ marginTop: 6 }} onClick={() => set({ rows: [...form.rows, blankRow()] })}>Add a quantity</Button>

        <Collapse ghost style={{ marginTop: 12 }} items={[{
          key: "more", label: "Allowances, coordination, alternates and target",
          children: (
            <>
              {skippable.map(([k, a]) => (
                <div key={k} style={{ marginBottom: 6 }}>
                  <Checkbox checked={!!form.skip[a.skipIf]} onChange={(e) => set({ skip: { ...form.skip, [a.skipIf]: e.target.checked } })}>
                    Do not carry <b>{k}</b> ({a.hours} h)
                  </Checkbox>
                  {form.skip[a.skipIf] ? <Input size="small" style={{ marginTop: 4 }} placeholder="Why - printed on the estimate" value={form.reasons[a.skipIf] || ""} onChange={(e) => set({ reasons: { ...form.reasons, [a.skipIf]: e.target.value } })} /> : null}
                </div>
              ))}
              <Row gutter={12} style={{ marginTop: 8 }}>
                {[["rounds", "Clash rounds"], ["meetings", "Coordination meetings"], ["rfis", "RFIs"]].map(([k, l]) => (
                  <Col key={k} xs={12} md={6}><Typography.Text type="secondary">{l}</Typography.Text><InputNumber min={0} style={{ width: "100%" }} value={form.coordination[k]} onChange={(v) => set({ coordination: { ...form.coordination, [k]: v || 0 } })} /></Col>
                ))}
                <Col xs={12} md={6}><Typography.Text type="secondary">Oversight, % of hours</Typography.Text><InputNumber min={0} max={50} style={{ width: "100%" }} value={form.oversight.percent} onChange={(v) => set({ oversight: { percent: v || 0 } })} /></Col>
                <Col xs={12} md={6} style={{ marginTop: 8 }}><Typography.Text type="secondary">Coordination window, weeks</Typography.Text><InputNumber min={0} style={{ width: "100%" }} value={form.windowWeeks} onChange={(v) => set({ windowWeeks: v || 0 })} /></Col>
                <Col xs={12} md={6} style={{ marginTop: 8 }}><Typography.Text type="secondary">Target fee (scope, never rates)</Typography.Text><InputNumber min={0} style={{ width: "100%" }} value={form.targetFee} onChange={(v) => set({ targetFee: v })} /></Col>
              </Row>
              <Typography.Title level={5} style={{ marginTop: 12 }}>Add-alternates</Typography.Title>
              {form.alternates.map((a, i) => (
                <Space key={a.key} style={{ marginBottom: 4 }} wrap>
                  <Checkbox checked={form.selectedAlternates.includes(a.key)} onChange={(e) => set({ selectedAlternates: e.target.checked ? [...form.selectedAlternates, a.key] : form.selectedAlternates.filter((k) => k !== a.key) })} />
                  <Input size="small" style={{ width: 240 }} value={a.label} placeholder="Level 2" onChange={(e) => set({ alternates: form.alternates.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} />
                  <InputNumber size="small" min={0} value={a.hours} addonAfter="h" onChange={(v) => set({ alternates: form.alternates.map((x, j) => (j === i ? { ...x, hours: v || 0 } : x)) })} />
                </Space>
              ))}
              <div><Button size="small" onClick={() => set({ alternates: [...form.alternates, { key: `alt${form.alternates.length + 1}`, label: "", hours: 0 }] })}>Add an alternate</Button></div>
              <Space style={{ marginTop: 8 }} wrap>
                <span>Bundle discount</span>
                <InputNumber size="small" min={0} max={50} value={form.bundle.percent} addonAfter="%" onChange={(v) => set({ bundle: { ...form.bundle, percent: v || 0 } })} />
                <span>when at least</span>
                <InputNumber size="small" min={2} value={form.bundle.minCount} onChange={(v) => set({ bundle: { ...form.bundle, minCount: v || 2 } })} />
                <span>are taken</span>
              </Space>
              <Typography.Title level={5} style={{ marginTop: 12 }}>Extra assumptions</Typography.Title>
              <Input.TextArea rows={2} value={form.notes} placeholder="One per line. Added to the generated assumptions, never replacing them." onChange={(e) => set({ notes: e.target.value })} />
            </>
          ),
        }]} />

        <Space style={{ marginTop: 12 }}>
          <Button onClick={runPreview} loading={busy}>Preview</Button>
          <Button type="primary" onClick={save} loading={busy} disabled={!preview}>{form.seriesId ? "Save as the next revision" : "Save draft"}</Button>
          {!preview ? <span style={{ color: "#888" }}>Preview first; the saved draft runs the same calculation.</span> : null}
        </Space>

        {preview ? <PreviewPanel r={preview} /> : null}
      </Card>
    </div>
  );
}

// The organisation's published rate card (21.4). Roles price hour
// buckets; a no-charge role still shows its hours on the estimate.
// Saving needs pricing rights - the server refuses it otherwise.
const BUCKETS = [["modeling", "Modelling"], ["coordination", "Coordination"], ["oversight", "Oversight"]];
function RateCardEditor({ projectId, onSaved, canEdit }) {
  const [card, setCard] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api(`/projects/${projectId}/estimates/rate-card`).then((d) => setCard(d.card || { roles: [], bucketRoles: {}, agreementRef: "" }))
      .catch(() => setCard({ roles: [], bucketRoles: {}, agreementRef: "" }));
  }, [projectId]);
  if (!card) return null;
  const roles = card.roles || [];
  const setRole = (i, patch) => setCard({ ...card, roles: roles.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const save = async () => {
    setSaving(true);
    try {
      const d = await api(`/projects/${projectId}/estimates/rate-card`, { method: "PUT", body: JSON.stringify({ roles, bucketRoles: card.bucketRoles || {}, agreementRef: card.agreementRef || "", name: card.name }) });
      setCard(d.card); message.success("Rate card saved"); onSaved();
    } catch (e) { message.error(e.message); } finally { setSaving(false); }
  };
  return (
    <Collapse style={{ marginTop: 16 }} items={[{
      key: "card",
      label: <span>Rate card {roles.length ? <span style={{ color: "#888" }}>· {roles.length} role{roles.length === 1 ? "" : "s"}{card.agreementRef ? ` · ${card.agreementRef}` : ""}</span> : <Tag color="orange">not set - estimates are unpriced</Tag>}</span>,
      children: (
        <>
          {roles.map((r, i) => (
            <Space key={i} style={{ marginBottom: 4 }} wrap>
              <Input size="small" style={{ width: 110 }} placeholder="key" value={r.key} onChange={(e) => setRole(i, { key: e.target.value })} />
              <Input size="small" style={{ width: 220 }} placeholder="Coordination Engineer" value={r.label} onChange={(e) => setRole(i, { label: e.target.value })} />
              <InputNumber size="small" min={0} addonBefore="$" addonAfter="/h" value={r.rate} disabled={r.noCharge} onChange={(v) => setRole(i, { rate: v || 0 })} />
              <Checkbox checked={!!r.noCharge} onChange={(e) => setRole(i, { noCharge: e.target.checked })}>No charge</Checkbox>
              {r.noCharge ? <Input size="small" style={{ width: 240 }} placeholder="Covered by ..." value={r.noChargeNote} onChange={(e) => setRole(i, { noChargeNote: e.target.value })} /> : null}
              <Button size="small" type="text" onClick={() => setCard({ ...card, roles: roles.filter((_, j) => j !== i) })}>x</Button>
            </Space>
          ))}
          <div><Button size="small" onClick={() => setCard({ ...card, roles: [...roles, { key: "", label: "", rate: 0 }] })}>Add a role</Button></div>
          <Space style={{ marginTop: 10 }} wrap>
            {BUCKETS.map(([b, l]) => (
              <span key={b}>{l} is priced as{" "}
                <Select size="small" style={{ width: 170 }} value={(card.bucketRoles || {})[b]} placeholder="role"
                        options={roles.filter((r) => r.key).map((r) => ({ value: r.key, label: r.label || r.key }))}
                        onChange={(v) => setCard({ ...card, bucketRoles: { ...(card.bucketRoles || {}), [b]: v } })} />
              </span>
            ))}
          </Space>
          <div style={{ marginTop: 10 }}>
            <Input size="small" style={{ maxWidth: 360 }} addonBefore="Governing agreement" value={card.agreementRef} onChange={(e) => setCard({ ...card, agreementRef: e.target.value })} />
          </div>
          <Button size="small" type="primary" style={{ marginTop: 10 }} loading={saving} disabled={!canEdit} onClick={save}>Save rate card</Button>
          {!canEdit ? <span style={{ marginLeft: 8, color: "#888" }}>Needs pricing rights on this project.</span> : null}
        </>
      ),
    }]} />
  );
}

// Who can price this project, and (for the project owner or an admin) who
// its PM is. PMs are the owner company's own people; partners never are.
function PricingPanel({ projectId, pricing }) {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api(`/projects/${projectId}/estimates/pms`).then((d) => { setData(d); setPicked((d.pms || []).map((p) => p._id)); }).catch(() => setData(null));
  }, [projectId]);
  const as = { admin: "an admin", owner: "the project owner", pm: "this project's PM" }[pricing.as];
  const save = async () => {
    setSaving(true);
    try { await api(`/projects/${projectId}/estimates/pms`, { method: "PUT", body: JSON.stringify({ userIds: picked }) }); message.success("Project PM saved"); }
    catch (e) { message.error(e.message); } finally { setSaving(false); }
  };
  return (
    <Alert style={{ marginTop: 16 }} type={pricing.ok ? "success" : "info"} showIcon
      message={pricing.ok ? `You can price this project, as ${as}.` : "You can build drafts; approving and rates need pricing rights (an admin, the project owner or this project's PM)."}
      description={data ? (
        <div>
          <span>Project PM: {(data.pms || []).length ? data.pms.map((p) => p.name || p.email).join(", ") : "none set"}</span>
          {data.canAppoint ? (
            <Space style={{ marginLeft: 12 }} wrap>
              <Select mode="multiple" size="small" style={{ minWidth: 280 }} value={picked} placeholder="Owner-company people"
                      options={(data.candidates || []).map((u) => ({ value: u._id, label: u.name || u.email }))}
                      optionFilterProp="label" onChange={setPicked} />
              <Button size="small" loading={saving} onClick={save}>Save PM</Button>
            </Space>
          ) : null}
        </div>
      ) : null} />
  );
}
